import { workspaceLoaderPhase } from '../lib/workspaceLoaderPhase';
import { useWorkspaceHydrationDeadline } from '../lib/useWorkspaceHydrationDeadline';
import { recordWorkspaceLoadStage } from '../lib/workspaceLoadTelemetry';
import React, { useEffect, useState } from 'react';
import { auth, signInWithGoogle } from '../firebase';
import { onAuthStateChanged, User, getIdTokenResult } from 'firebase/auth';
import { Button } from './ui/button';
import { LogIn, Loader2, Shield } from 'lucide-react';
import { UnifiedLoader } from './ui/UnifiedLoader';
import { WelcomeFirstLogin, isWelcomePending } from './WelcomeFirstLogin';
import { ResumeUploadsBanner } from './ResumeUploadsBanner';
import { LoginScreen } from './auth/LoginScreen';
import { isLocalMockMode, isEmulatorMode, LOCAL_DEV_EMAIL, LOCAL_DEV_PASSWORD } from '../lib/localDev/isLocalMockMode';
import { useStore } from '../store';
import { keepSignedInOnDevice } from '../lib/authDevicePreference';
import { useKeepSignedInOnDevice } from '../lib/useKeepSignedInOnDevice';

// v1.12.8 — pre-login MARKETING landing. Lazy so the unauthenticated first-paint
// path (which already lazy-loads TwoFactorChallenge below) doesn't eagerly pull
// the marketing chrome (marketing.css + the product-preview rotator) into the
// entry chunk for every user. Only logged-out users at the site root pay for it.
const LandingPage = React.lazy(() =>
  import('./marketing/LandingPage').then((m) => ({ default: m.LandingPage })),
);

/**
 * Run the post-fetch one-shot migrations inside a setInitialMigration
 * bracket so the burst of writes doesn't fan out into N synchronous
 * recomputes (Fix_React_Error_185_Prompt). The deferred batch sweeps
 * readiness afterward at 200ms/site, and bulkClassify continues as a
 * fire-and-forget. Awaits both migrations sequentially so the bracket
 * is open for the whole replay window.
 */
/**
 * SED_Architecture_Unification SA5 — AuthGate backstop. Scan every site
 * once after auth; a site inactive for 30 minutes is reconciled by the server,
 * which reads its current lease before deciding whether the job was abandoned.
 * Browser snapshots never have authority to fail an active server worker.
 *
 * Best-effort, fire-and-forget. Runs alongside the SA4 subscriber so the
 * subscriber's toast will fire as the backstop's updateSite lands.
 */
async function runStuckBootstrapBackstop(
  store: typeof import('../store')['useStore'],
): Promise<void> {
  try {
    const { isAbandonedBootstrap } = await import('../lib/sedStaleness');
    const state = store.getState();
    const now = Date.now();
    for (const site of state.siteRag.sites) {
      if (!isAbandonedBootstrap(site, now)) continue;
      try {
        const { authedFetch } = await import('../lib/authedFetch');
        const response = await authedFetch(`/api/sed/bootstrap/active/${encodeURIComponent(site.id)}`);
        if (!response.ok) throw new Error(`Bootstrap reconciliation HTTP ${response.status}`);
        // The live site listener receives any lease-guarded server correction.
        // An unreachable server is not evidence that the worker has failed.
      } catch (e) {
        console.warn(`[stuck-bootstrap-backstop] update failed for ${site.id}:`, (e as Error)?.message);
      }
    }
  } catch (e) {
    console.warn('[stuck-bootstrap-backstop] scan failed:', (e as Error)?.message);
  }
}

/**
 * SED_Architecture_Unification SA4 — wire a one-time subscription that
 * fires success/failure toasts whenever a site's `sedStatus` flips out
 * of 'bootstrapping'. Pairs with the loading toast `rebuildSiteSed`
 * fires on click — the user sees instant click feedback, then a final
 * verdict toast when the bootstrap settles.
 *
 * Module-level flag so HMR / re-renders don't stack subscribers.
 */
let __sedStatusSubscriberInstalled = false;

/**
 * D0.1 LAYER 2 — module-level handle for the recurring stuck-bootstrap
 * backstop interval, mirroring `__sedStatusSubscriberInstalled` above. Without
 * it, every AuthGate remount / HMR reload would stack another 60s scanner.
 */
let __stuckBackstopIntervalId: ReturnType<typeof setInterval> | null = null;

/** D0.1 — period of the recurring stuck-bootstrap backstop scan. */
export const STUCK_BACKSTOP_INTERVAL_MS = 60_000;

/**
 * P4-5a — module-level handle for the inactivity auto-logout timer, mirroring
 * `__stuckBackstopIntervalId` above. Without it, every AuthGate remount / HMR
 * reload would stack another idle timer (and every activity event would race to
 * reset the wrong handle).
 */
let __idleLogoutTimeoutId: ReturnType<typeof setTimeout> | null = null;

/** P4-5a — idle window after which a signed-in user is auto-signed-out (30 min). */
export const IDLE_LOGOUT_TIMEOUT_MS = 30 * 60_000;

/** Coalesce frequent activity events without losing their actual idle deadline. */
const IDLE_ACTIVITY_BROADCAST_INTERVAL_MS = 1_000;
const IDLE_ACTIVITY_CHANNEL = 'datacrate-auth-idle-activity-v1';

/**
 * P4-5a — `window` events that count as "user activity" and reset the idle
 * timer. `visibilitychange` is handled separately because it fires on
 * `document`, not `window`.
 */
export const IDLE_ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/**
 * P4-5a — install a client-side inactivity auto-logout. While a user is signed
 * in, any of `IDLE_ACTIVITY_EVENTS` resets a single 30-min timer; if it ever
 * fires (no interaction for the full window) the supplied `signOut` runs. The
 * timer logic is extracted from the AuthGate effect so the gating / reset /
 * cleanup contract is unit testable without rendering the gated component.
 *
 *   - No-ops (returns a no-op cleanup) when there is no signed-in user or in
 *     local mock mode (offline auth is never signed out by telemetry).
 *   - Module-guards against stacked timers across HMR / remount: a second
 *     install while one is live is a no-op.
 *   - Defensive against missing DOM (SSR / test env without `window`).
 *   - `signOut` is invoked at most once per fired window; the timer is cleared
 *     before it runs so a late activity event can't re-arm a dead session.
 *
 * @param hasUser    whether a user is currently signed in.
 * @param signOut    the sign-out action to run when the idle window elapses.
 * @param timeoutMs  override for the idle window (defaults to 30 min).
 * @param userUid    same-account tabs share activity; omitted preserves local-only behavior.
 */
export function installInactivityLogout(
  hasUser: boolean,
  signOut: () => void | Promise<void>,
  timeoutMs: number = IDLE_LOGOUT_TIMEOUT_MS,
  userUid?: string,
): () => void {
  if (!hasUser || isLocalMockMode()) return () => {};
  if (__idleLogoutTimeoutId !== null) return () => {}; // already running
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};

  const activityWindow = window;
  const activityDocument = document;
  let active = true;
  let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let broadcastTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let channel: BroadcastChannel | null = null;
  let lastActivityAt = Date.now();
  let lastLocalActivityAt = lastActivityAt;
  let lastBroadcastAt = -Infinity;

  const closeChannel = () => {
    if (broadcastTimeoutId !== null) clearTimeout(broadcastTimeoutId);
    broadcastTimeoutId = null;
    if (!channel) return;
    try { channel.removeEventListener('message', receiveActivity); } catch { /* best effort */ }
    try { channel.close(); } catch { /* keep the local idle timer */ }
    channel = null;
  };

  const stop = () => {
    if (!active) return;
    active = false;
    for (const evt of IDLE_ACTIVITY_EVENTS) activityWindow.removeEventListener(evt, reset);
    activityDocument.removeEventListener('visibilitychange', reset);
    if (idleTimeoutId !== null) {
      clearTimeout(idleTimeoutId);
      if (__idleLogoutTimeoutId === idleTimeoutId) __idleLogoutTimeoutId = null;
      idleTimeoutId = null;
    }
    closeChannel();
  };

  const arm = () => {
    if (idleTimeoutId !== null) clearTimeout(idleTimeoutId);
    idleTimeoutId = setTimeout(() => {
      // Retire this installation before signing out: late events / cleanup
      // cannot re-arm it or clear a later user's timer.
      stop();
      try {
        void Promise.resolve(signOut()).catch(() => {});
      } catch {
        // Sign-out is best-effort; onAuthStateChanged drives the UI either way.
      }
    }, Math.max(0, lastActivityAt + timeoutMs - Date.now()));
    __idleLogoutTimeoutId = idleTimeoutId;
  };

  const broadcastActivity = () => {
    broadcastTimeoutId = null;
    if (!active || !channel) return;
    try {
      channel.postMessage({ type: 'activity', uid: userUid, at: lastLocalActivityAt });
      lastBroadcastAt = Date.now();
    } catch {
      closeChannel(); // unsupported / closed channel falls back to local activity
    }
  };

  const reset = () => {
    if (!active) return;
    lastActivityAt = Date.now();
    lastLocalActivityAt = lastActivityAt;
    arm();
    if (!channel || broadcastTimeoutId !== null) return;
    const delay = Math.max(0, IDLE_ACTIVITY_BROADCAST_INTERVAL_MS - (Date.now() - lastBroadcastAt));
    if (delay === 0) broadcastActivity();
    else broadcastTimeoutId = setTimeout(broadcastActivity, delay);
  };

  const receiveActivity = (event: MessageEvent<unknown>) => {
    if (!active || !channel) return;
    const data = event.data;
    if (typeof data !== 'object' || data === null || Array.isArray(data)) return;
    const message = data as Record<string, unknown>;
    if (Object.keys(message).sort().join(',') !== 'at,type,uid'
      || message.type !== 'activity' || message.uid !== userUid) return;
    const at = message.at;
    const now = Date.now();
    if (typeof at !== 'number' || !Number.isSafeInteger(at) || at <= lastActivityAt
      || at > now || now - at >= timeoutMs) return;
    lastActivityAt = at;
    arm(); // use the source event time, never extend idle time by delivery delay
    // Received activity is not rebroadcast, avoiding cross-tab echo loops.
  };

  if (typeof userUid === 'string' && userUid.length > 0 && typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(IDLE_ACTIVITY_CHANNEL);
      channel.addEventListener('message', receiveActivity);
    } catch {
      closeChannel();
    }
  }
  for (const evt of IDLE_ACTIVITY_EVENTS) {
    activityWindow.addEventListener(evt, reset, { passive: true });
  }
  // `visibilitychange` fires on `document` (not `window`). Treating it as a
  // reset is intentional: returning to / focusing the tab re-arms a fresh
  // window so a backgrounded-then-resumed tab isn't logged out the instant the
  // user comes back. Idle time only accrues while no events fire at all.
  activityDocument.addEventListener('visibilitychange', reset, { passive: true });
  reset(); // arm the initial window

  return stop;
}

/**
 * D0.1 LAYER 2 — install the recurring stuck-bootstrap backstop interval.
 * Returns a cleanup function (or a no-op when not installed). Extracted from
 * the AuthGate effect so the gating / module-guard / cleanup contract is unit
 * testable without rendering the whole gated component.
 *
 *   - No-ops (returns a no-op cleanup) when there is no signed-in user or in
 *     local mock mode.
 *   - Module-guards against stacked intervals across HMR / remount: a second
 *     install while one is live is a no-op.
 *   - Each tick re-runs runStuckBootstrapBackstop, which force-fails only
 *     `isAbandonedBootstrap` sites (30-min ceiling) and no-ops the rest.
 */
export function installStuckBootstrapBackstopInterval(
  hasUser: boolean,
  store: typeof import('../store')['useStore'],
): () => void {
  if (!hasUser || isLocalMockMode()) return () => {};
  if (__stuckBackstopIntervalId !== null) return () => {}; // already running
  const id = setInterval(() => {
    void runStuckBootstrapBackstop(store);
  }, STUCK_BACKSTOP_INTERVAL_MS);
  __stuckBackstopIntervalId = id;
  return () => {
    clearInterval(id);
    if (__stuckBackstopIntervalId === id) __stuckBackstopIntervalId = null;
  };
}

/**
 * Guard so the explainer-hints login counter (`recordLogin`) increments at
 * most once per page load. `onAuthStateChanged` can fire its callback more than
 * once for the same load (token refresh, claim re-read), and we only want to
 * count one "login" per app session.
 */
let __loginRecordedThisLoad = false;
function installSedStatusSubscriber(
  store: typeof import('../store')['useStore'],
): void {
  if (__sedStatusSubscriberInstalled) return;
  // Zustand stores expose `.subscribe`, but some tests mock `useStore` with
  // only `{ getState }`. Calling `.subscribe` on those throws inside the
  // fire-and-forget IIFE, which surfaces as an unhandled rejection that
  // fails the vitest run (3144 tests pass / 1 error → exit 1). Bail out
  // cleanly when no subscribe seam is available.
  if (typeof (store as unknown as { subscribe?: unknown }).subscribe !== 'function') return;
  __sedStatusSubscriberInstalled = true;
  void (async () => {
    // sonner's toast is best-effort UI feedback. If the import fails (no
    // sonner in scope), or any individual toast call throws (no Toaster
    // mounted in a test env, or a transient error), the subscriber must
    // still keep ticking — the bootstrap status flow is the source of
    // truth, the toast is decoration.
    let toast: typeof import('sonner')['toast'] | null;
    try {
      ({ toast } = await import('sonner'));
    } catch {
      toast = null;
    }
    let prev = store.getState().siteRag.sites.map(s => ({ id: s.id, status: s.sedStatus }));
    store.subscribe((state) => {
      const curr = state.siteRag.sites.map(s => ({ id: s.id, status: s.sedStatus }));
      for (const { id, status } of curr) {
        const before = prev.find(p => p.id === id);
        if (!before) continue;
        try {
          if (before.status === 'bootstrapping' && (status === 'complete' || status === 'partial')) {
            toast?.success('SED rebuilt', { id: `sed-rebuild-${id}` });
          } else if (before.status === 'bootstrapping' && status === 'failed') {
            toast?.error('SED rebuild failed', { id: `sed-rebuild-${id}` });
          }
        } catch {
          // Toast call failed (eg. no Toaster mounted in a test
          // env, no DOM). Subscriber must keep ticking.
        }
      }
      prev = curr;
    });
  })();
}

export async function runPostAuthMigration(
  store: typeof import('../store')['useStore'],
  options: { runReportDocsMigration: boolean },
): Promise<void> {
  const s = store.getState();
  s.setInitialMigration(true);
  try {
    await s.migrateLegacySiteSeds();
    if (options.runReportDocsMigration) {
      await s.migrateLegacyReportDocuments();
    }
    // Run after migrateLegacyReportDocuments so any docs the latter has
    // copied to site.documents are visible to this migration's "already
    // present" check; we only promote-and-clear stragglers that
    // genuinely never made it to the site (the Financial Model writer
    // bug surface).
    await s.migrateFinancialDocsToSite();
    // Dashboard_Zero_Report_Filter_Fix ZR2 — heal any site whose
    // `reportIds` array drifted behind the actual `report.siteId` joins
    // (the dashboard's "Sites without reports" misclassification). Runs
    // inside the migration bracket so its `updateSite` writes share the
    // suppressed-recompute window; a near no-op on a warm reload.
    await s.reconcileSiteReportIds();
    // SED_Stale_Data_Rebuild SD1 — stamp `sedComputedAt` on legacy sites
    // that have an SED but no build timestamp (best-effort: uses the
    // site's `updatedAt`). Lets the stale-SED detection + bulk-rebuild
    // (SD3) work against historical sites. Idempotent — a fully-stamped
    // portfolio is a zero-write no-op.
    await s.backfillSedComputedAt();
  } finally {
    store.getState().setInitialMigration(false);
  }
  // SA4 — install the success/failure toast subscriber once sites are
  // loaded.
  installSedStatusSubscriber(store);
  // SA5 — sweep any sites still stuck in 'bootstrapping' from a previous
  // session. Runs after the subscriber installs so the toast fires when
  // the backstop flips the status. Fire-and-forget.
  void runStuckBootstrapBackstop(store);
  // Readiness recompute is throttled via runDeferredReadinessRecompute
  // (Fix_React_Error_185_Prompt). Once it drains, kick off the doc-tag
  // backfill (Evidence_Setup_Auto_And_Modes ES1) so existing sites recover
  // their Docs % on this same page load — no AdminPanel click required.
  // Chained (not fired in parallel) so the two throttled loops don't both
  // pump setTimeouts at once and double the per-tick update budget. The
  // backfill's own per-site idempotency guard makes a warm reload near-free.
  void store.getState().runDeferredReadinessRecompute()
    .then(() => store.getState().runDeferredDocClassifierBackfill())
    .then(() => {
      const companyId = store.getState().companyId;
      if (companyId) {
        return store.getState().ensurePortfolioComposites(companyId);
      }
    })
    .catch(e => console.warn('[docClassifier] deferred backfill chain failed:', (e as Error)?.message));
  // Bulk-classify lands as before — independent of the migration bracket.
  import('../services/knowledge/bulkClassifyOnLoad')
    .then(({ runBulkClassifyOnLoad }) => {
      void runBulkClassifyOnLoad(store.getState().siteRag.sites);
    })
    .catch(e => console.warn('[bulkClassify] dispatch failed:', (e as Error)?.message));
}

/**
 * Test-only re-export. Lets `AuthGate.migration.test.tsx` invoke the
 * helper without spinning up the full AuthGate component. Production
 * callers use `runPostAuthMigration` directly via the import above.
 */
export const __runPostAuthMigrationForTest = (options: { runReportDocsMigration: boolean }) =>
  // The store mock injected by the test file replaces the inner
  // `useStore` import — we resolve the real import lazily so the
  // mock has time to register.
  import('../store').then(({ useStore }) => runPostAuthMigration(useStore, options));

/**
 * Hydrate the authenticated workspace in authority order.
 *
 * Company scope must resolve before the site/report reads start. Both reads
 * use `store.companyId` to include company-shared records; starting them while
 * that value is still null produces a plausible-looking but incomplete
 * workspace on a fresh browser session. In particular, shared sites can load
 * without their shared reports, which makes Path to GO compute an
 * "Insufficient data" verdict until the page is reloaded.
 *
 * Keep the two heavier collection reads parallel once scope is known. Brand
 * and optional user settings hydration are independent at that point and can
 * share the same parallel window.
 */
export async function hydrateCompanyScopedWorkspaceData(
  store: typeof import('../store')['useStore'],
  options: { hydrateUserSettings?: boolean } = {},
): Promise<void> {
  recordWorkspaceLoadStage('auth_ready');
  try {
    await store.getState().loadCompanyData();
  } catch (error) {
    // A company-context lookup failure must not strand personal sites/reports
    // behind an infinite data loader. The authoritative collection reads still
    // run in their own-only fallback scope; a later auth refresh can recover
    // company sharing.
    console.warn('[AuthGate] company scope hydration failed; loading own workspace only:', error);
  }
  recordWorkspaceLoadStage('company_scope_ready', {
    companyId: (store.getState() as { companyId?: string | null }).companyId ?? null,
  });

  const scoped = store.getState();
  const sitesPromise = scoped.fetchSites();
  const reportsPromise = scoped.fetchRagReports();
  const sitesSummaryReadyPromise = (async () => {
    const hasLiveStoreSubscribe =
      typeof (store as { subscribe?: unknown }).subscribe === 'function'
      && Boolean((store.getState() as { siteRag?: unknown }).siteRag);
    if (!hasLiveStoreSubscribe) {
      await sitesPromise;
      return;
    }
    if (store.getState().siteRag?.isSitesLoaded) {
      return;
    }
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        unsub();
        resolve();
      };
      const unsub = store.subscribe((state) => {
        if (state.siteRag?.isSitesLoaded) {
          finish();
        }
      });
      if (store.getState().siteRag?.isSitesLoaded) {
        finish();
        return;
      }
      void sitesPromise.finally(finish);
    });
  })();
  const authoritativeReads = Promise.all([
    sitesSummaryReadyPromise,
    reportsPromise,
  ]);
  const bestEffort: Promise<unknown>[] = [scoped.loadBrandLogos()];
  if (scoped.companyId && typeof (scoped as { loadPortfolioComposites?: unknown }).loadPortfolioComposites === 'function') {
    bestEffort.push(scoped.loadPortfolioComposites(scoped.companyId));
  }
  if (options.hydrateUserSettings) bestEffort.push(scoped.hydrateUserSettings());
  void Promise.allSettled(bestEffort);
  await authoritativeReads;
  const finalState = store.getState() as {
    siteRag?: { sites?: unknown[]; reports?: unknown[] };
  };
  recordWorkspaceLoadStage('dashboard_first_render', {
    siteCount: finalState.siteRag?.sites?.length ?? 0,
    reportCount: finalState.siteRag?.reports?.length ?? 0,
  });
}

interface AuthGateProps {
  children: React.ReactNode;
}

/**
 * Module-level MFA resolver slot. The `LoginScreen` sets this via the
 * `setPendingMfaResolver` setter exported below when sign-in throws
 * `auth/multi-factor-auth-required`; the gate consumes it to show the
 * `TwoFactorChallenge` screen and clears it on either success or cancel.
 *
 * A simple module-scope handoff is cleaner than threading a context
 * through every login surface — only one MFA flow is ever in flight at
 * a time.
 */
let __pendingMfaResolver: import('firebase/auth').MultiFactorResolver | null = null;
const __pendingMfaListeners = new Set<() => void>();
export function setPendingMfaResolver(
  resolver: import('firebase/auth').MultiFactorResolver | null,
): void {
  __pendingMfaResolver = resolver;
  __pendingMfaListeners.forEach((l) => l());
}
function usePendingMfaResolver(): import('firebase/auth').MultiFactorResolver | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    const listener = () => setTick((n) => n + 1);
    __pendingMfaListeners.add(listener);
    return () => { __pendingMfaListeners.delete(listener); };
  }, []);
  return __pendingMfaResolver;
}

export function AuthGate({ children }: AuthGateProps) {
  const [user, setUser] = useState<User | null>(null);
  const keepSignedIn = useKeepSignedInOnDevice(user?.uid);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [authCheckAttempt, setAuthCheckAttempt] = useState(0);
  // Bumped after WelcomeFirstLogin saves so the gate re-renders without the flag.
  const [welcomeAck, setWelcomeAck] = useState(0);
  const pendingMfaResolver = usePendingMfaResolver();
  const [dataPhase, setDataPhase] = useState(false);

  // v1.12.8 — logged-out users at the SITE ROOT ('/') see the marketing landing
  // first; clicking "Sign in" / "Enter Datacrate" flips to the LoginScreen. Any
  // DEEP-LINK (any non-'/' path) initializes straight to 'login' so the landing
  // never intercepts a direct link into the app. Initialized once from the
  // pathname at mount — auth deep-links don't change the pathname, so this is a
  // stable, render-safe seed (matches the lazy-MFA pattern below).
  const [authView, setAuthView] = useState<'landing' | 'login'>(
    typeof window !== 'undefined' && window.location.pathname === '/' ? 'landing' : 'login',
  );

  const isSitesLoaded = useStore((s) => s.siteRag.isSitesLoaded);
  const isReportsLoaded = useStore((s) => s.siteRag.isReportsLoaded);
  const isReportMetadataLoaded = useStore((s) => s.siteRag.isReportMetadataLoaded);

  // The normal loader waits for both collections. Its single fallback
  // deadline survives either collection finishing first; the dashboard still
  // enforces completeness after the overlay releases on scoped report data.
  const bodiesOverdue = useWorkspaceHydrationDeadline({
    authenticatedUserId: user?.uid ?? null,
    dataPhase,
    isSitesLoaded,
    isReportsLoaded,
  });

  const phase = workspaceLoaderPhase({
    loading,
    dataPhase,
    isSitesLoaded,
    isReportsLoaded,
    isReportMetadataLoaded,
    bodiesOverdue,
  });

  // LOCAL DEV MOCK MODE (hard dev-gated via isLocalMockMode → statically
  // stripped from prod builds): bypass Firebase auth entirely and seed a
  // bundled offline dataset so the app runs with NO login while cloud
  // services are unavailable. See src/lib/localDev/localMockData.ts.
  const [mockReady, setMockReady] = useState(false);
  useEffect(() => {
    if (!isLocalMockMode()) return;
    let cancelled = false;
    void Promise.all([
      import('../lib/localDev/localMockData'),
      import('../store'),
    ]).then(async ([{ loadLocalMockData }, { useStore }]) => {
      if (cancelled) return;
      loadLocalMockData(useStore);
      // Browser simulation fixtures are a second, explicit opt-in layered on
      // top of local mock mode. Keep the import behind Vite's compile-time DEV
      // constant so no production bundle can install or reach the driver.
      if (import.meta.env.DEV) {
        const { isSimulationE2eMode } = await import('../lib/localDev/isLocalMockMode');
        if (isSimulationE2eMode()) {
          const { installSimulationE2eDriver } = await import('../lib/localDev/e2eSimulationDriver');
          if (cancelled) return;
          installSimulationE2eDriver(useStore);
        }
      }
      setLoading(false);
      setMockReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // LOCAL DEV EMULATOR MODE: auto-sign-in the seeded dev user against the Auth
  // emulator so there's no login screen AND a REAL auth.currentUser (so writes
  // persist to the local Firestore — unlike mock mode). The normal
  // onAuthStateChanged flow below then loads data from the emulator. Gate is
  // statically false in prod builds.
  useEffect(() => {
    if (!isEmulatorMode()) return;
    let cancelled = false;
    void (async () => {
      const { auth } = await import('../firebase');
      // Already signed in as the target dev fixture? nothing to do.
      if (auth.currentUser?.email?.toLowerCase() === LOCAL_DEV_EMAIL.toLowerCase()) return;
      const { signInWithEmailAndPassword, signOut } = await import('firebase/auth');
      try {
        // A *different* persisted emulator session (e.g. a previously
        // auto-signed-in account) would otherwise be trapped active by the
        // old `if (currentUser) return` guard. Sign it out first so a reload
        // deterministically lands on the seeded fixture account.
        if (auth.currentUser) await signOut(auth);
        if (cancelled) return;
        await signInWithEmailAndPassword(auth, LOCAL_DEV_EMAIL, LOCAL_DEV_PASSWORD);
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[emulators] dev auto-sign-in failed — run `npm run seed:local` or `npm run seed:local:rich` first.', (e as Error)?.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fallback allowed emails — used until custom claims are set up
  const ALLOWED_EMAILS = (import.meta.env.VITE_ALLOWED_EMAILS || 'william.theron@datacrate.io')
    .split(',')
    .map((e: string) => e.trim().toLowerCase())
    .filter(Boolean);

  useEffect(() => {
    // Local mock mode bypasses Firebase auth entirely — never touch the
    // (offline / suspended) auth service.
    if (isLocalMockMode()) return;
    // PH-A2.12 — track the last-seen uid across auth callbacks so we can
    // clear the module-scope dedupeFetch map on every uid boundary
    // (null → uid, uid → null, uid-a → uid-b). Without this an
    // in-flight fetchSites / fetchRagReports started under user A and
    // not yet settled when A signs out stays in the map forever; user B
    // would then receive A's cached Promise instead of issuing a fresh
    // fetch against B's data.
    let dataTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastUid: string | null = null;
    let disposed = false;
    let checkSequence = 0;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      const checkId = ++checkSequence;
      const isCurrentCheck = () => !disposed && checkId === checkSequence
        && auth.currentUser?.uid === currentUser?.uid;
      setAuthUnavailable(false);
      if (dataTimeout) { clearTimeout(dataTimeout); dataTimeout = null; }
      const nextUid = currentUser?.uid ?? null;
      if (nextUid !== lastUid) {
        lastUid = nextUid;
        setUser(null);
        setDataPhase(false);
        setLoading(true);
        try {
          const { resetInFlightFetches, resetReportGenerationJobsSubscription, resetSentimentEvidenceWaits, resetSitesSubscription } = await import('../store');
          resetInFlightFetches();
          useStore.setState((state) => ({ siteRag: { ...state.siteRag, isSitesLoaded: false, isReportsLoaded: false, isReportMetadataLoaded: false, hydratedReportSiteIds: [] } }));
          resetReportGenerationJobsSubscription();
          // A Public Sentiment run held for site evidence (in-memory, never
          // dispatched) belongs to the previous session: stop its watcher so
          // it can never be sent under the next user's token or after logout.
          resetSentimentEvidenceWaits();
          // LIVE-SITES — tear down the real-time sites listener(s) on every uid
          // boundary (logout / user-swap) so user B never inherits user A's
          // live site channel (RBAC) and no listener leaks.
          resetSitesSubscription();
        } catch {
          // store import shouldn't fail; if it does we still proceed
          // with auth handling — better than blocking the gate.
        }
      }
      if (!isCurrentCheck()) return;
      if (!currentUser) {
        setUser(null);
        setDataPhase(false);
        setLoading(false);
        return;
      }

      const attachWorkspaceSubscriptions = (uid: string) => {
        if (!isCurrentCheck() || auth.currentUser?.uid !== uid) return;
        const state = useStore.getState();
        if (typeof state.subscribeReportGenerationJobs === 'function') {
          state.subscribeReportGenerationJobs(uid);
        }
        if (typeof state.subscribeSites === 'function') {
          state.subscribeSites(uid);
          if (!state.companyId && typeof (useStore as { subscribe?: unknown }).subscribe === 'function') {
            const stopCompanyWatch = useStore.subscribe((s) => {
              if (!s.companyId) return;
              stopCompanyWatch();
              import('../firebase').then(({ auth: liveAuth }) => {
                if (liveAuth.currentUser?.uid === uid) {
                  useStore.getState().subscribeSites(uid);
                }
              });
            });
          }
        }
      };

      const startWorkspaceDataLoad = (options: {
        runReportDocsMigration: boolean;
        hydrateUserSettings?: boolean;
      }) => {
        setDataPhase(true);
        void hydrateCompanyScopedWorkspaceData(useStore, {
          hydrateUserSettings: options.hydrateUserSettings,
        }).then(() => {
          if (!isCurrentCheck()) return;
          setLoading(false);
          attachWorkspaceSubscriptions(currentUser.uid);
          return runPostAuthMigration(useStore, {
            runReportDocsMigration: options.runReportDocsMigration,
          });
        }).catch(() => {
          if (isCurrentCheck()) {
            setLoading(false);
            attachWorkspaceSubscriptions(currentUser.uid);
          }
        });
        dataTimeout = setTimeout(() => {
          if (isCurrentCheck()) setLoading(false);
        }, 8000);
      };

      // Check custom claims first (role-based access)
      try {
        const tokenResult = await Promise.race([
          getIdTokenResult(currentUser, true),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Auth timeout')), 8000)),
        ]);
        if (!isCurrentCheck()) return;
        const role = tokenResult.claims.role as string | undefined;

        if (role) {
          // User has a role claim — they're authorized
          setUser(currentUser);
          setUnauthorized(false);

          // Store role in Zustand
          useStore.setState({ userRole: role });
          startWorkspaceDataLoad({ runReportDocsMigration: true });
        } else if (ALLOWED_EMAILS.includes((currentUser.email || '').toLowerCase())) {
          // No role claim but email is in allowed list — authorized (legacy)
          setUser(currentUser);
          setUnauthorized(false);

          useStore.setState({ userRole: 'admin' }); // bootstrap admin
          startWorkspaceDataLoad({ runReportDocsMigration: true });
        } else {
          // Not authorized
          import('../firebase').then(({ logOut }) => logOut());
          setUser(null);
          setUnauthorized(true);
          setLoading(false);
          return;
        }
      } catch (err) {
        if (!isCurrentCheck()) return;
        // Token refresh failed — retry once before falling back
        console.warn('Auth service slow or unavailable, retrying...', err);
        try {
          const retryResult = await Promise.race([
            getIdTokenResult(currentUser, true),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Retry timeout')), 5000)),
          ]);
          if (!isCurrentCheck()) return;
          const retryRole = retryResult.claims.role as string | undefined;
          if (retryRole) {
            setUser(currentUser);
            setUnauthorized(false);
            useStore.setState({ userRole: retryRole });
            startWorkspaceDataLoad({
              runReportDocsMigration: false,
              hydrateUserSettings: true,
            });
            import('../config/reportTypes').then(({ initReportTypeOverrides }) => initReportTypeOverrides());
            import('../services/knowledge/peerReviewDb').then(({ hydrateCuratedKnowledge }) => hydrateCuratedKnowledge());
          } else if (ALLOWED_EMAILS.includes((currentUser.email || '').toLowerCase())) {
            // Bootstrap admin only — viewer role until claims are properly set
            setUser(currentUser);
            setUnauthorized(false);
            useStore.setState({ userRole: 'admin' });
            startWorkspaceDataLoad({
              runReportDocsMigration: false,
              hydrateUserSettings: true,
            });
            import('../config/reportTypes').then(({ initReportTypeOverrides }) => initReportTypeOverrides());
            import('../services/knowledge/peerReviewDb').then(({ hydrateCuratedKnowledge }) => hydrateCuratedKnowledge());
          } else {
            setUser(null);
            setUnauthorized(true);
            setLoading(false);
            return;
          }
        } catch (retryError) {
          if (!isCurrentCheck()) return;
          // Both attempts failed — fail closed unless bootstrap email
          if (ALLOWED_EMAILS.includes((currentUser.email || '').toLowerCase())) {
            setUser(currentUser);
            setUnauthorized(false);
            setLoading(false);
            import('../store').then(({ useStore }) => {
              useStore.setState({ userRole: 'viewer' }); // restricted, not admin
              void hydrateCompanyScopedWorkspaceData(useStore, {
                hydrateUserSettings: true,
              }).then(() => {
                attachWorkspaceSubscriptions(currentUser.uid);
                return runPostAuthMigration(useStore, {
                  runReportDocsMigration: false,
                });
              });
              import('../config/reportTypes').then(({ initReportTypeOverrides }) => initReportTypeOverrides());
              import('../services/knowledge/peerReviewDb').then(({ hydrateCuratedKnowledge }) => hydrateCuratedKnowledge());
            });
          } else {
            // An unavailable claims response is not proof of missing access.
            // Keep the app blocked; Retry obtains fresh claims via this effect.
            const code = typeof retryError === 'object' && retryError !== null
              && 'code' in retryError ? retryError.code : undefined;
            const rejected = code === 'auth/user-disabled'
              || code === 'auth/user-token-expired'
              || code === 'auth/invalid-user-token'
              || code === 'auth/invalid-credential';
            setUser(null);
            setDataPhase(false);
            setUnauthorized(rejected);
            setAuthUnavailable(!rejected);
            setLoading(false);
            console.warn('[auth] claims check did not complete', retryError);
            return;
          }
        }
      }

      // Inline explainer/help cards default ON for the first few logins, then
      // OFF (unless the user explicitly toggles in the UserMenu). Count each
      // authenticated app load exactly once per page load — module-level guard
      // so re-fired onAuthStateChanged callbacks (token refresh, etc.) on the
      // same load don't inflate the count.
      if (currentUser && !__loginRecordedThisLoad) {
        __loginRecordedThisLoad = true;
        import('../lib/explainerHintsPref').then(({ recordLogin }) => recordLogin());
      }

      // Load API keys from Firestore
      if (currentUser) {
        import('../store').then(({ useStore }) => {
          import('../firebase').then(({ db }) => {
            import('firebase/firestore').then(({ doc, getDoc }) => {
              getDoc(doc(db, `users/${currentUser.uid}/settings`, 'apiKeys')).then(snap => {
                if (snap.exists()) {
                  const keys = snap.data();
                  const local = useStore.getState().userApiKeys;
                  useStore.getState().setUserApiKeys({ ...keys, ...local });
                }
              }).catch(() => {});
            });
          });
        });
      }
    });
    return () => {
      disposed = true;
      unsubscribe();
      if (dataTimeout) clearTimeout(dataTimeout);
    };
  }, [authCheckAttempt]);

  // D0.1 LAYER 2 — recurring stuck-bootstrap backstop. The original
  // `runStuckBootstrapBackstop` runs once at login (in runPostAuthMigration);
  // that one-shot can't heal a site whose job dies while the tab stays open
  // (no reload to re-trigger it). Re-scan every 60s while a user is signed in
  // and force-fail any `isAbandonedBootstrap` site (30-min ceiling). Bounded
  // write amplification: isAbandonedBootstrap no-ops on non-bootstrapping
  // sites, so already-failed sites never re-write; the SA4 toast subscriber
  // dedupes on `sed-rebuild-${id}` so a reaped site shows one toast, not one
  // per tick. The module-level guard prevents stacked intervals across HMR.
  useEffect(() => {
    return installStuckBootstrapBackstopInterval(Boolean(user), useStore);
  }, [user]);

  // Device opt-in retires the app's idle timer, never Firebase authentication.
  // Preference/account changes clean up the old timer and its cross-tab channel.
  useEffect(() => {
    const uid = user?.uid;
    if (keepSignedIn) return;
    let active = true;
    const cleanup = installInactivityLogout(Boolean(uid), () =>
      import('../firebase').then(({ auth, logOut }) => {
        // A preference change can race an already queued timeout/import.
        if (active && auth.currentUser?.uid === uid && !keepSignedInOnDevice(uid)) return logOut();
      }),
      IDLE_LOGOUT_TIMEOUT_MS,
      uid,
    );
    return () => { active = false; cleanup(); };
  }, [user?.uid, keepSignedIn]);

  // LOCAL DEV MOCK MODE — render the app with no auth once the offline
  // dataset is seeded. The gate is statically false in prod builds, so this
  // branch (and the auth bypass it implies) cannot exist in production.
  if (isLocalMockMode()) {
    if (!mockReady) {
      return <UnifiedLoader phase="auth" />;
    }
    return (
      <>
        {children}
        <UnifiedLoader phase={phase} />
      </>
    );
  }

  // We remove the early `if (loading)` return here, because we want to 
  // render the UnifiedLoader overlay over the app if user exists, or just the loader if no user.
  // We will handle it after the `!user` check, or right here if no user.
  if (loading && !user) {
    return <UnifiedLoader phase={phase} />;
  }


  if (authUnavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md space-y-4 text-center" role="status" aria-live="polite">
          <h1 className="text-xl font-semibold text-text-primary">Sign-in check unavailable</h1>
          <p className="text-sm text-text-secondary">
            We could not check your account permissions. Try again when the connection is available.
          </p>
          <Button onClick={() => {
            setAuthUnavailable(false);
            setUnauthorized(false);
            setUser(null);
            setDataPhase(false);
            setLoading(true);
            setAuthCheckAttempt((attempt) => attempt + 1);
          }}>Retry sign-in check</Button>
        </div>
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="h-screen w-screen bg-background flex items-center justify-center font-mono">
        <div className="max-w-md w-full mx-4 space-y-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-danger/10 border-2 border-danger/30 flex items-center justify-center">
              <Shield className="w-8 h-8 text-danger" />
            </div>
            <h1 className="text-xl font-bold uppercase tracking-[0.3em] text-text-primary">Access Denied</h1>
          </div>
          <div className="border-2 border-danger/30 bg-background p-6 space-y-4 text-center">
            <p className="text-[10px] text-text-secondary uppercase tracking-widest leading-relaxed">
              Your account is not authorized to access this platform. Datacrate is currently in private beta.
            </p>
            <p className="text-[9px] text-text-muted uppercase tracking-widest">
              Contact admin@datacrate.io for access.
            </p>
            <Button
              variant="outline"
              onClick={signInWithGoogle}
              className="h-10 rounded-md uppercase tracking-widest text-[10px] font-bold"
            >
              <LogIn className="w-3.5 h-3.5 mr-2" /> Try Another Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    // Emulator mode auto-signs-in the dev user; show a loader (not the login
    // screen) during that brief window so there's no login flash.
    if (isEmulatorMode()) {
      return (
        <div className="h-screen w-screen bg-background flex items-center justify-center font-mono">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-text-primary animate-spin" />
            <span className="text-[10px] text-text-muted uppercase tracking-widest">Local emulator — signing in…</span>
          </div>
        </div>
      );
    }
    if (pendingMfaResolver) {
      // Lazy-imported so the bundle for unauthenticated users that
      // never trigger MFA stays small.
      const TwoFactorChallenge = React.lazy(() =>
        import('./auth/TwoFactorChallenge').then(m => ({ default: m.TwoFactorChallenge }))
      );
      return (
        <React.Suspense fallback={
          <div className="h-screen w-screen bg-background flex items-center justify-center font-mono">
            <Loader2 className="w-6 h-6 animate-spin text-text-primary" />
          </div>
        }>
          <TwoFactorChallenge
            resolver={pendingMfaResolver}
            onCancel={() => setPendingMfaResolver(null)}
            onSuccess={() => setPendingMfaResolver(null)}
          />
        </React.Suspense>
      );
    }
    // v1.12.8 — at the site root, an unauthenticated user lands on the marketing
    // page; "Sign in" / "Enter Datacrate" flip `authView` to 'login'. Deep-links
    // seeded `authView` to 'login' at mount, so they fall straight through here
    // to the LoginScreen with NO landing flash. Lazy-loaded (see import) and
    // wrapped in Suspense, mirroring the MFA-challenge branch above.
    if (authView === 'landing') {
      return (
        <React.Suspense fallback={
          <div className="h-screen w-screen bg-background flex items-center justify-center font-mono">
            <Loader2 className="w-6 h-6 animate-spin text-text-primary" />
          </div>
        }>
          <LandingPage onSignIn={() => setAuthView('login')} />
        </React.Suspense>
      );
    }
    return <LoginScreen />;
  }

  // First-login welcome — set by InviteLanding after a magic-link accept.
  // welcomeAck is referenced so React re-evaluates this branch after onComplete.
  if (isWelcomePending()) {
    void welcomeAck;
    return <WelcomeFirstLogin onComplete={() => setWelcomeAck(n => n + 1)} />;
  }

  // Banner sits above the app shell. Self-contained: queries
  // listIncompleteBatches() once auth resolves; renders nothing when
  // there's nothing to resume. See Resumable_Document_Upload_Prompts
  // Prompt 3.
  return (
    <>
      <ResumeUploadsBanner />
      {children}
      <UnifiedLoader phase={phase} onEscape={() => setLoading(false)} />
    </>
  );
}

// LoginScreen extracted to src/components/auth/LoginScreen.tsx by
// R-142-AUTH-UX Phase A. The component is imported at the top of
// this file and used inside the AuthGate render.
